import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import {
  Keyboard,
  type KeyboardEvent,
  type NativeScrollEvent,
  type NativeSyntheticEvent,
  Platform,
  ScrollView,
  type ScrollViewProps,
  type StyleProp,
  StyleSheet,
  TextInput,
  View,
  type ViewStyle,
} from 'react-native';

/** Space kept between the field being typed in and the top of the keyboard. */
const GAP_ABOVE_KEYBOARD = 24;

type Props = ScrollViewProps & {
  /** Extra space under the focused field, e.g. for a hint or error line below it. */
  extraScrollHeight?: number;
  /** Style for the wrapper around the ScrollView (it fills its parent by default). */
  containerStyle?: StyleProp<ViewStyle>;
};

/**
 * A ScrollView for forms that keeps the field being typed in above the keyboard.
 *
 * When the keyboard opens it measures how much of this view the keyboard covers (so headers,
 * safe areas and tab bars above or below need no offsets), adds that much room under the content,
 * and scrolls the focused field into view. Tapping another field while the keyboard is up scrolls
 * to that one too. Works the same on iOS and Android, edge-to-edge or not.
 */
export const KeyboardAwareScrollView = forwardRef<ScrollView, Props>(function KeyboardAwareScrollView(
  { children, contentContainerStyle, containerStyle, onScroll, extraScrollHeight = 0, keyboardShouldPersistTaps = 'handled', ...rest },
  ref,
) {
  const scrollRef = useRef<ScrollView>(null);
  useImperativeHandle(ref, () => scrollRef.current as ScrollView);
  const containerRef = useRef<View>(null);
  const offsetRef = useRef(0);
  const keyboardTopRef = useRef<number | null>(null);
  const [keyboardRoom, setKeyboardRoom] = useState(0);

  const revealFocused = useCallback(() => {
    const keyboardTop = keyboardTopRef.current;
    const input = TextInput.State.currentlyFocusedInput();
    if (keyboardTop == null || !input) return;
    input.measureInWindow((_x, y, _w, h) => {
      const overlap = y + h + GAP_ABOVE_KEYBOARD + extraScrollHeight - keyboardTop;
      if (overlap > 0) scrollRef.current?.scrollTo({ y: offsetRef.current + overlap, animated: true });
      // A field scrolled up behind the top edge (e.g. the first field of a long form) comes back too.
      else if (y < 0) scrollRef.current?.scrollTo({ y: Math.max(0, offsetRef.current + y - GAP_ABOVE_KEYBOARD), animated: true });
    });
  }, [extraScrollHeight]);

  useEffect(() => {
    const onShow = (e: KeyboardEvent) => {
      keyboardTopRef.current = e.endCoordinates.screenY;
      containerRef.current?.measureInWindow((_x, y, _w, h) => {
        setKeyboardRoom(Math.max(0, y + h - e.endCoordinates.screenY));
        // After the added room has been laid out, so there is space to scroll into.
        setTimeout(revealFocused, 60);
      });
    };
    const onHide = () => {
      keyboardTopRef.current = null;
      setKeyboardRoom(0);
    };
    const subs = [
      Keyboard.addListener(Platform.OS === 'ios' ? 'keyboardWillShow' : 'keyboardDidShow', onShow),
      Keyboard.addListener(Platform.OS === 'ios' ? 'keyboardWillHide' : 'keyboardDidHide', onHide),
    ];
    return () => subs.forEach((s) => s.remove());
  }, [revealFocused]);

  const handleScroll = (e: NativeSyntheticEvent<NativeScrollEvent>) => {
    offsetRef.current = e.nativeEvent.contentOffset.y;
    onScroll?.(e);
  };

  return (
    <View
      ref={containerRef}
      style={[styles.fill, containerStyle]}
      collapsable={false}
      // Moving to another field while the keyboard is already up fires no keyboard event; the tap does.
      onTouchEnd={() => {
        if (keyboardTopRef.current != null) setTimeout(revealFocused, 250);
      }}>
      <ScrollView
        ref={scrollRef}
        {...rest}
        keyboardShouldPersistTaps={keyboardShouldPersistTaps}
        onScroll={handleScroll}
        scrollEventThrottle={rest.scrollEventThrottle ?? 16}
        contentContainerStyle={[contentContainerStyle, keyboardRoom > 0 && { paddingBottom: keyboardRoom + GAP_ABOVE_KEYBOARD }]}>
        {children}
      </ScrollView>
    </View>
  );
});

const styles = StyleSheet.create({
  fill: { flex: 1 },
});
